'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { useCreation } from '@ps/functions/hooks/useCreation';
import { cn } from '@ps/ui/utils';
import { BehaviorSubject } from 'rxjs';

import {
  VideoPlayerContext,
  type VideoPlayerContextValue,
  useAudioMuteContext,
  useDispatchAction,
  useHideOverlay,
  useIsOverlayHidden,
  useIsVideoPaused,
} from '../contexts/video-player';
import {
  type AnyVideo,
  VideoType,
  isPopClipVideo,
  recordClipView,
} from '../functions';
import type { ButtonStyles } from './VideoFooter';
import { VideoOverlay } from './VideoOverlay';
import { VideoPlayer } from './VideoPlayer';
import { VideoTouchBehavior } from './VideoTouchBehavior';

// Module-level dedup — prevents double-counting across remounts and StrictMode
const _recordedViewIds = new Set<string>();

type VideoContentProps = {
  video: AnyVideo;
  show?: boolean;
  play?: boolean;
  muted?: boolean;
  loop?: boolean;
  focused?: boolean;
  active?: boolean;
  currentUserId?: string;
  buttonStyles?: ButtonStyles;
  isRealEstateListing?: boolean;
  listingId?: string;
  onBack?: () => void;
  onOpenComments?: () => void;
  onUpdated?: (video: AnyVideo) => void;
  onDeleted?: (video: AnyVideo) => void;
  /** Optional node rendered in the footer info area — used to link the clip to
   *  its parent series / course. */
  ownerLink?: ReactNode;
  className?: string;
};

export function VideoContent({
  video: initialVideo,
  show = true,
  play = true,
  muted: mutedProp,
  loop,
  focused = true,
  active,
  currentUserId,
  buttonStyles,
  isRealEstateListing,
  listingId,
  onBack,
  onOpenComments,
  onUpdated,
  onDeleted,
  ownerLink,
  className,
}: VideoContentProps) {
  if (!show) {
    return <div className={cn('bg-surface-2', className)} />;
  }

  return (
    <VideoContentInner
      video={initialVideo}
      play={play}
      mutedProp={mutedProp}
      loop={loop}
      focused={focused}
      active={active}
      currentUserId={currentUserId}
      buttonStyles={buttonStyles}
      isRealEstateListing={isRealEstateListing}
      listingId={listingId}
      onBack={onBack}
      onOpenComments={onOpenComments}
      onUpdated={onUpdated}
      onDeleted={onDeleted}
      ownerLink={ownerLink}
      className={className}
    />
  );
}

function VideoContentInner({
  video: initialVideo,
  play,
  mutedProp,
  loop,
  focused,
  currentUserId,
  buttonStyles,
  isRealEstateListing,
  listingId,
  onBack,
  onOpenComments,
  onUpdated,
  onDeleted,
  ownerLink,
  className,
}: Omit<VideoContentProps, 'show'> & { mutedProp?: boolean }) {
  const [video, setVideo] = useState(initialVideo);
  const dispatchAction = useDispatchAction();

  // Update video state only when the actual video changes (by id
  // or key fields), not on every object reference change.
  useEffect(() => {
    setVideo(initialVideo);
  }, [
    initialVideo.id,
    (initialVideo as any)._isLockedForUser,
    initialVideo.videoURL,
    initialVideo.videoTitle,
    initialVideo.thumbnailURL,
  ]);

  // Create BehaviorSubjects for per-video context
  const positionMs$ = useCreation(() => new BehaviorSubject(0), []);
  const currentTime$ = useCreation(() => new BehaviorSubject(0), []);
  const totalDuration$ = useCreation(() => new BehaviorSubject(0), []);
  const muted$ = useCreation(() => new BehaviorSubject(false), []);
  const ready$ = useCreation(() => new BehaviorSubject(true), []);
  const loaded$ = useCreation(() => new BehaviorSubject(false), []);
  const loading$ = useCreation(() => new BehaviorSubject(false), []);
  const videoRef$ = useCreation(
    () =>
      new BehaviorSubject<React.RefObject<HTMLVideoElement | null> | undefined>(
        undefined,
      ),
    [],
  );

  const [loaded] = useBehaviorSubject(loaded$);
  const { muted$: globalMuted$ } = useAudioMuteContext();
  const [globalMuted] = useBehaviorSubject(globalMuted$);
  const paused = useIsVideoPaused();
  const isOverlayHidden = useIsOverlayHidden();
  const hideOverlay = useHideOverlay();

  const onLoad = useCallback(() => {
    loaded$.next(true);
    if (isPopClipVideo(video)) {
      const clipId = video.clipId ?? video.id;
      if (clipId && !_recordedViewIds.has(clipId)) {
        _recordedViewIds.add(clipId);
        void recordClipView(clipId);
      }
    }
  }, [loaded$, video]);

  const shouldPlay = !paused && Boolean(play && focused);

  const contextValue: VideoPlayerContextValue = {
    video,
    videoRef$,
    loaded$,
    loading$,
    ready$,
    positionMs$,
    currentTime$,
    totalDuration$,
    muted$,
  };

  // Auto-hide overlay once after 15 seconds — use ref so toggling back on doesn't re-trigger
  // Skip auto-hide for calendar-tagged videos — the calendar widget is the primary interaction.
  const taggedItemType = isPopClipVideo(video)
    ? (video.taggedItemType ?? video.metaData?.taggedItemType)
    : undefined;
  const autoHiddenRef = useRef(false);
  useEffect(() => {
    if (video.type !== VideoType.Clips) return;
    if (isRealEstateListing) return;
    if (taggedItemType === 'calendar') return;
    autoHiddenRef.current = false;

    const sub = positionMs$.subscribe(ms => {
      if (ms >= 15000 && !autoHiddenRef.current) {
        autoHiddenRef.current = true;
        hideOverlay();
      }
    });
    return () => sub.unsubscribe();
  }, [
    positionMs$,
    hideOverlay,
    video.type,
    taggedItemType,
    isRealEstateListing,
  ]);

  // Preview-duration access control: play normally, then notify
  // the host (e.g. paywall, login modal) at the preview limit.
  const isLocked = Boolean((initialVideo as any)._isLockedForUser);
  const rawPreview = Number((initialVideo as any).previewDuration);
  const previewDurationMs = isLocked
    ? (Number.isFinite(rawPreview) ? rawPreview : 3) * 1000
    : null;
  const lockTriggeredRef = useRef(false);

  useEffect(() => {
    if (!isLocked || previewDurationMs == null) return;
    lockTriggeredRef.current = false;

    // previewDuration=0 means no preview at all — lock immediately
    if (previewDurationMs <= 0) {
      lockTriggeredRef.current = true;
      dispatchAction({ type: 'lockedVideoTapped', video: initialVideo });
      return;
    }

    const sub = positionMs$.subscribe(ms => {
      if (ms >= previewDurationMs && !lockTriggeredRef.current) {
        lockTriggeredRef.current = true;
        dispatchAction({ type: 'lockedVideoTapped', video: initialVideo });
      }
    });
    return () => sub.unsubscribe();
  }, [isLocked, previewDurationMs, positionMs$, initialVideo, dispatchAction]);

  return (
    <VideoPlayerContext.Provider value={contextValue}>
      <VideoTouchBehavior
        className={cn('bg-surface-2 relative overflow-hidden', className)}
        disableOverlayToggle={taggedItemType != null}
      >
        {/* Cover image / background */}
        {video.thumbnailURL && !loaded && (
          <div className="absolute inset-0">
            <img
              src={video.thumbnailURL}
              alt=""
              className="h-full w-full object-cover blur-lg"
            />
            <div className="bg-scrim/40 absolute inset-0" />
          </div>
        )}

        {/* Video player */}
        <VideoPlayer
          video={video}
          shouldPlay={shouldPlay}
          muted={mutedProp ?? globalMuted}
          loop={loop}
          onLoad={onLoad}
        />

        {/* Top gradient - left and right fade */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[5]"
          style={{
            height: '45%',
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)',
          }}
        />

        {/* Bottom gradient - always visible for text readability */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[5]"
          style={{
            height: '45%',
            background:
              'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)',
          }}
        />

        {/* Content overlay */}
        <VideoOverlay
          video={video}
          visible={
            Boolean(play) && (isRealEstateListing === true || !isOverlayHidden)
          }
          currentUserId={currentUserId}
          buttonStyles={buttonStyles}
          isRealEstateListing={isRealEstateListing}
          listingId={listingId}
          onBack={onBack}
          onOpenComments={onOpenComments}
          onUpdated={onUpdated}
          onDeleted={onDeleted}
          ownerLink={ownerLink}
        />
      </VideoTouchBehavior>
    </VideoPlayerContext.Provider>
  );
}
